import { Box, Typography, Divider } from '@mui/material';
import {
  useAppDispatch,
  useAppSelector,
} from '../../../app/store/configureStore';
import { CartOrderSummary } from '../../../features/customer/cart/CartOrderSummary';
import { CartItemList } from '../../../features/customer/cart/CartItemList';
import { Cart, CartItem } from '../../../app/data/models/cart';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import OrderTypeSelector from '../../../features/customer/cart/OrderTypeSelector';
import { OrderType } from '../../../app/data/models/orders/orderType';
import { isValidSwissPhoneNumber } from '../../../app/utils/phoneNumberValidator';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import EmptyScreen from '../../../app/components/EmptyScreen';
import LoadingComponent from '../../../app/layout/LoadingComponent';
import ListTitleBox from '../../../app/components/list/ListTitleBox';
import {
  createWaiterOrderAsync,
  loadRestaurantCartsAsync,
  updateCartDetails,
  updateProductQuantity,
} from './waiterOrderSlice';
import { useRestaurant } from '@app/hooks/useRestaurant';

const WaiterCartPage = () => {
  const { t } = useTranslation();
  const { restaurantId, cartId } = useParams();
  const { restaurant, isLoading: isRestaurantLoading } = useRestaurant(
    restaurantId!
  );
  const { carts, status } = useAppSelector((state) => state.waiterOrder);
  const cart = carts.find((c: Cart) => c.id === cartId);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [orderType, setOrderType] = useState<OrderType>(
    cart?.orderType || OrderType.DineIn
  );
  const [customerName, setCustomerName] = useState(cart?.customerName || '');
  const [customerPhone, setCustomerPhone] = useState(cart?.customerPhone || '');
  const [phoneNumberError, setPhoneNumberError] = useState<string | null>(null);
  const [customerNameError, setCustomerNameError] = useState<string | null>(
    null
  );

  useEffect(() => {
    if (!restaurantId) return;
    dispatch(loadRestaurantCartsAsync(restaurantId));
  }, [dispatch, restaurantId]);

  const [tableNumber, setTableNumber] = useState<number | undefined>(() => {
    return cart?.tableNumber ? Number(cart.tableNumber) : undefined;
  });

  const [tableNumberError, setTableNumberError] = useState<string | null>(null);

  const handleTableNumberChange = (newTableNumber: number | undefined) => {
    setTableNumber(newTableNumber);
  };

  const handleQuantityChange = (cartItem: CartItem, quantity: number) => {
    dispatch(
      updateProductQuantity({
        cartItem: { ...cartItem },
        quantity,
        restaurantId,
        cartId,
      })
    );
  };

  const validateForm = (): boolean => {
    if (orderType === OrderType.TakeAway) {
      if (!customerName.trim()) {
        setCustomerNameError(t('cart.errors.name-required'));
        return false;
      }
      if (!customerPhone) {
        setPhoneNumberError(t('cart.errors.phone-required'));
        return false;
      }
      if (!isValidSwissPhoneNumber(customerPhone)) {
        setPhoneNumberError(t('cart.errors.phone-invalid'));
        return false;
      }
    } else if (orderType === OrderType.DineIn) {
      if (!tableNumber) {
        setTableNumberError(t('cart.errors.table-number-required'));
        return false;
      }
    }
    return true;
  };

  const header = (
    <ListTitleBox
      title={t('cart.title')}
      onBackClick={() =>
        navigate(
          `/restaurant-waiter/restaurants/${restaurantId}/orders/create/${cartId}`
        )
      }
    />
  );

  useEffect(() => {
    if (restaurantId && cartId) {
      dispatch(
        updateCartDetails({
          cartId,
          restaurantId: restaurantId,
          updates: {
            orderType,
            customerName,
            customerPhone,
            tableNumber,
          },
        })
      );
    }
  }, [orderType, customerName, customerPhone, tableNumber]);

  if (status.includes('pending') || isRestaurantLoading)
    return <LoadingComponent />;

  if (!cart) {
    return (
      <EmptyScreen
        title={t('cart.title')}
        emptyText={t('cart.no-items')}
        onBackClick={() =>
          navigate(
            `/restaurant-waiter/restaurants/${restaurantId}/orders/create/${cartId}`
          )
        }
      />
    );
  }

  const handleCheckout = async () => {
    if (!validateForm()) return;

    try {
      const order = await dispatch(
        createWaiterOrderAsync({
          cart: {
            id: cart.id,
            restaurantId: cart.restaurantId,
            restaurantName: cart.restaurantName,
            orderItems: cart.orderItems,
            orderType,
            tableNumber:
              orderType === OrderType.DineIn ? tableNumber : undefined,
            customerName:
              orderType === OrderType.TakeAway ? customerName : undefined,
            customerPhone:
              orderType === OrderType.TakeAway ? customerPhone : undefined,
            totalPrice: cart.totalPrice,
            totalQuantity: cart.totalQuantity,
            waiterId: cart.waiterId,
            waiterName: cart.waiterName,
          },
        })
      ).unwrap();

      if (!order) return;
      navigate(
        `/restaurant-waiter/restaurants/${restaurantId}/orders/${order.id}`
      );
    } catch (error) {
      console.error('Failed to create order:', error);
      toast.error(t('cart.errors.create-order'));
    }
  };

  if (cart.orderItems.length === 0) {
    return (
      <>
        {header}
        <Box sx={{ width: '100%', mx: 'auto', p: 4 }}>
          <Typography variant="h5" textAlign="center">
            {t('cart.no-items')}
          </Typography>
        </Box>
      </>
    );
  }

  return (
    <>
      {header}
      <Box sx={{ width: '100%', px: 0 }}>
        <Typography variant="subtitle1" color="text.secondary" gutterBottom>
          {cart.restaurantName}
        </Typography>

        <Divider sx={{ my: 2 }} />

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 4,
            alignItems: { xs: 'stretch', md: 'flex-start' },
          }}
        >
          <Box sx={{ flex: 1 }}>
            <CartItemList
              cartItems={cart.orderItems}
              defaultTaxRate={cart.defaultTaxRate}
              onQuantityChange={handleQuantityChange}
            />
          </Box>
          <Box sx={{ width: { xs: '100%', md: '380px' } }}>
            <OrderTypeSelector
              orderType={orderType}
              tableNumber={tableNumber}
              tableNumberError={tableNumberError}
              customerName={customerName}
              customerPhone={customerPhone}
              onOrderTypeChange={setOrderType}
              onTableNumberChange={handleTableNumberChange}
              onCustomerNameChange={setCustomerName}
              onCustomerPhoneChange={(value) => {
                setCustomerPhone(value);
                setPhoneNumberError(null);
              }}
              phoneNumberError={phoneNumberError}
              customerNameError={customerNameError}
            />
            <CartOrderSummary
              cart={cart}
              status={status}
              tableNumber={tableNumber}
              orderType={orderType}
              onCheckout={handleCheckout}
              priceIncludesTax={restaurant?.settings?.priceIncludesTax ?? false}
            />
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default WaiterCartPage;
